import type {Ref} from 'vue';
import {computed, ref} from 'vue';

import type {
  IPagination,
  IPaginationFilters,
} from '@/assets/core/assets/api/types';

import useRequestHook from '@/assets/core/assets/hooks/requestHook';

import type {IQueryObject} from '@/assets/core/assets/helpers/serialize';
import {serializeFromObjectToQueryString} from '@/assets/core/assets/helpers/serialize';
import type {
  IRequestPaginationHookCache,
  TRequestHookUrl,
} from '@/assets/core/assets/hooks/types';

const defaultPage = 1;
const defaultPageSize = 50;

const useRequestPaginationHook = <
  TGet,
  TFilters extends IQueryObject = IQueryObject,
  TAdditions = unknown,
>(
  baseUrl: TRequestHookUrl,
  baseFilters?: TFilters,
  basePagination?: Omit<IPaginationFilters, 'page'>,
) => {
  const request = useRequestHook();

  const cache = ref<IRequestPaginationHookCache<TGet, TAdditions>>({});

  const currentFilters = ref<TFilters | undefined>(baseFilters) as Ref<
    TFilters | undefined
  >;
  const paginationFilters = ref<IPaginationFilters>({
    page: defaultPage,
    size: basePagination?.size ?? defaultPageSize,
  });

  const getBaseUrl = computed(() => new URL(baseUrl.toString()));
  const getUrlClear = computed(
    () => getBaseUrl.value.origin + getBaseUrl.value.pathname,
  );
  const getKeyCurrent = computed(() =>
    getKey(paginationFilters.value, currentFilters.value),
  );

  const getBaseFilters = computed(() => baseFilters);
  const getFilters = computed(() => currentFilters.value);
  const getPagination = computed(() => paginationFilters.value);
  const getTotal = computed(() => {
    return cache.value[getKeyCurrent.value]?.total ?? undefined;
  });

  const getPage = computed(() => {
    if (!cache.value[getKeyCurrent.value]) return [];
    return (
      cache.value[getKeyCurrent.value].pages[paginationFilters.value.page] ?? []
    );
  });

  const getPageWithPrev = computed(() => {
    if (!cache.value[getKeyCurrent.value]) return [];
    return (
      Object.entries(cache.value[getKeyCurrent.value].pages)
        .filter(([page]) => parseInt(page) <= paginationFilters.value.page)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(([_, value]): TGet[] => value)
        .flat(1) ?? []
    );
  });

  const getPageAll = computed(() => {
    if (!cache.value[getKeyCurrent.value]) return [];
    return Object.values(cache.value[getKeyCurrent.value].pages).flat(1) ?? [];
  });

  const getAdditions = computed(
    () => cache.value[getKeyCurrent.value]?.additions ?? ({} as TAdditions),
  );

  const getLoadingRequestWithCurrentFilters = computed(() => {
    return Object.values(request.getRequestsDetail.value)
      .filter((item) => item.loading)
      .find((item) => {
        const url = item.url.toString();

        return (
          url === getKeyCurrent.value ||
          (url.startsWith(getKeyCurrent.value) &&
            url.slice(getKeyCurrent.value.length + 1).startsWith('page='))
        );
      });
  });

  const isLoading = computed(() => request.isLoading.value);
  const isLoadingFirst = computed(() => {
    return (
      isLoading.value &&
      (getLoadingRequestWithCurrentFilters.value == null ||
        !Object.keys(cache.value[getKeyCurrent.value]?.pages ?? {}).length)
    );
  });
  const isLoadingNext = computed(() => {
    return (
      isLoading.value &&
      getLoadingRequestWithCurrentFilters.value != null &&
      !!Object.keys(cache.value[getKeyCurrent.value]?.pages ?? {}).length
    );
  });

  const isFullLoaded = computed(() => {
    const currentCache = cache.value[getKeyCurrent.value];
    const itemsCount = getPageAll.value.length;

    return (
      currentCache?.total === itemsCount ||
      paginationFilters.value.page * paginationFilters.value.size > itemsCount
    );
  });

  const isLastPage = computed(() => {
    const totalPages = Math.ceil(
      (cache.value[getKeyCurrent.value]?.total ?? 0) /
        paginationFilters.value.size,
    );
    return paginationFilters.value.page >= totalPages;
  });

  function getKey(pagination: IPaginationFilters, filters?: TFilters): string {
    const query: IQueryObject = {
      ...filters,
      size:
        pagination.size !== defaultPageSize
          ? pagination.size.toString()
          : undefined,
    };

    const stringQuery = serializeFromObjectToQueryString(query);
    return getUrlClear.value + (stringQuery ? `?${stringQuery}` : '');
  }

  function getRequestUrl(
    pagination: IPaginationFilters,
    filters?: TFilters,
  ): string {
    const key = getKey(pagination, filters);

    const query: IQueryObject = {
      page:
        pagination.page !== defaultPage
          ? pagination.page.toString()
          : undefined,
    };

    const stringQuery = serializeFromObjectToQueryString(query);

    const delimiter = new URL(key).search.length ? '&' : '?';
    return key + (stringQuery ? `${delimiter}${stringQuery}` : '');
  }

  const getFiltersWithCurrent = (filters?: TFilters) => {
    const obj = {
      ...(filters ?? currentFilters.value ?? ({} as TFilters)),
      ...baseFilters,
    };

    if ('size' in obj) delete obj.size;
    if ('page' in obj) delete obj.page;

    return obj;
  };
  const getPaginationWithCurrent = (
    pagination?: Partial<IPaginationFilters>,
  ) => ({
    page: pagination?.page ?? paginationFilters.value.page,
    size: pagination?.size ?? paginationFilters.value.size,
  });
  const getParamsWithCurrent = (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ) => ({
    filters: getFiltersWithCurrent(filters),
    pagination: getPaginationWithCurrent(pagination),
  });

  const isFiltersCurrent = (filters?: TFilters) => {
    return (
      JSON.stringify(currentFilters.value) === JSON.stringify(filters ?? {})
    );
  };
  const setFilters = (filters: TFilters) => (currentFilters.value = filters);
  const resetFilters = () => (currentFilters.value = undefined);

  const setPagination = (pagination: Partial<IPaginationFilters>) => {
    if (pagination.size != null) paginationFilters.value.size = pagination.size;
    if (pagination.page != null) paginationFilters.value.page = pagination.page;
  };
  const resetPagination = () =>
    (paginationFilters.value = {
      size: defaultPageSize,
      page: defaultPage,
    });

  const loadRequest = async (
    url: TRequestHookUrl,
  ): Promise<IPagination<TGet>> => {
    return await request.get(url);
  };

  const loadPage = async (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ): Promise<TGet[]> => {
    const params = getParamsWithCurrent(filters, pagination);

    const key = getKey(params.pagination, params.filters);
    const url = getRequestUrl(params.pagination, params.filters);

    const data = await loadRequest(url);

    cache.value[key] = cache.value[key] ?? {
      pages: {},
      total: data.total,
      options: null,
    };

    cache.value[key].pages[params.pagination.page] = data.items;
    cache.value[key].additions = Object.fromEntries(
      Object.entries(data).filter(
        (entry) => !['items', 'total', 'size', 'page'].includes(entry[0]),
      ),
    ) as TAdditions;

    currentFilters.value = params.filters;
    paginationFilters.value = params.pagination;

    return data.items;
  };

  const loadPageWithCache = async (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ): Promise<TGet[]> => {
    const params = getParamsWithCurrent(filters, pagination);

    const key = getKey(params.pagination, params.filters);

    const list =
      cache.value[key] && cache.value[key].pages[params.pagination.page]
        ? cache.value[key].pages[params.pagination.page]
        : await loadPage(params.filters, params.pagination);

    currentFilters.value = params.filters;
    paginationFilters.value = params.pagination;

    return list;
  };

  const loadFirstPage = async (): Promise<TGet[]> => {
    return await loadPage(currentFilters.value as TFilters, {
      ...paginationFilters.value,
      page: defaultPage,
    });
  };

  const loadNextPage = async (): Promise<TGet[]> => {
    if (isLastPage.value) return [];

    return await loadPage(currentFilters.value as TFilters, {
      ...paginationFilters.value,
      page: paginationFilters.value.page + 1,
    });
  };

  const loadFirstPageWithCache = async (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ): Promise<TGet[]> => {
    return await loadPageWithCache(filters, {
      ...pagination,
      page: defaultPage,
    });
  };

  const loadNextPageWithCache = async (
    filters?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ): Promise<TGet[]> => {
    if (isLastPage.value) return [];

    return await loadPageWithCache(filters, {
      ...pagination,
      page: paginationFilters.value.page + 1,
    });
  };

  const loadAllPage = async (): Promise<TGet[]> => {
    let data = await loadFirstPage();

    while (!isFullLoaded.value && data.length !== 0) {
      data = await loadNextPage();
    }

    return getPageAll.value;
  };

  const loadAllPageWithCache = async (
    filter?: TFilters,
    pagination?: Partial<IPaginationFilters>,
  ): Promise<TGet[]> => {
    let data = await loadFirstPageWithCache(filter, pagination);

    while (!isFullLoaded.value && data.length !== 0) {
      data = await loadNextPageWithCache(filter, pagination);
    }

    return getPageAll.value;
  };

  return {
    isLoading,
    isLoadingFirst,
    isLoadingNext,
    isFullLoaded,
    isLastPage,

    getFilters,
    getBaseFilters,
    getPagination,
    getTotal,
    getPage,
    getPageWithPrev,
    getPageAll,
    getAdditions,

    setFilters,
    resetFilters,
    isFiltersCurrent,
    setPagination,
    resetPagination,
    loadPage,
    loadPageWithCache,
    loadAllPage,
    loadAllPageWithCache,
    loadFirstPage,
    loadNextPage,
    loadFirstPageWithCache,
    loadNextPageWithCache,
  };
};

export default useRequestPaginationHook;
